import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ArrowLeft, Loader2, AlertTriangle, DollarSign } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { formatDateFull } from '../../utils/timeFormat';

const fmt = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);

export default function OverdueSettlements() {
  const navigate = useNavigate();
  const [data, setData] = useState({ overdue: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | campaign | backlog
  const [noteDrafts, setNoteDrafts] = useState({});
  const [savingNote, setSavingNote] = useState(null);

  const saveNote = async (bookingId) => {
    setSavingNote(bookingId);
    try {
      const note = noteDrafts[bookingId] ?? '';
      await api.post(`/admin/settlements/${bookingId}/status-note`, { note });
      setData((prev) => ({
        ...prev,
        overdue: prev.overdue.map((r) => r.booking_id === bookingId ? { ...r, status_note: note.trim() || null } : r),
      }));
      toast.success(note.trim() ? 'Status note posted — talent notified.' : 'Status note cleared.');
    } catch (e) {
      toast.error('Could not save the status note.');
    } finally {
      setSavingNote(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/settlements/overdue');
        setData(res.data);
      } catch (e) {
        toast.error('Could not load overdue settlements.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const rows = (data.overdue || []).filter((r) =>
    filter === 'all' ? true : filter === 'campaign' ? r.in_notification_campaign : !r.in_notification_campaign
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-overdue-settlements-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/admin/home')} className="text-cyan-600">
            <ArrowLeft className="w-4 h-4 mr-2" /> Admin Home
          </Button>
          <h1 className="text-lg font-bold text-gray-900">Overdue Settlements</h1>
          <div className="w-24" />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white border-gray-200 shadow-sm"><CardContent className="py-4"><p className="text-xs text-gray-500">Past due</p><p className="text-2xl font-bold text-rose-600">{data.summary?.count ?? 0}</p></CardContent></Card>
          <Card className="bg-white border-gray-200 shadow-sm"><CardContent className="py-4"><p className="text-xs text-gray-500">Total outstanding</p><p className="text-2xl font-bold text-gray-900">{fmt(data.summary?.total_amount)}</p></CardContent></Card>
          <Card className="bg-white border-gray-200 shadow-sm"><CardContent className="py-4"><p className="text-xs text-gray-500">In notification campaign</p><p className="text-2xl font-bold text-amber-600">{data.summary?.in_campaign ?? 0}</p></CardContent></Card>
          <Card className="bg-white border-gray-200 shadow-sm"><CardContent className="py-4"><p className="text-xs text-gray-500">Older backlog (silent)</p><p className="text-2xl font-bold text-gray-500">{data.summary?.backlog ?? 0}</p></CardContent></Card>
        </div>

        <div className="flex gap-2 mb-4">
          {[{ k: 'all', l: 'All' }, { k: 'campaign', l: 'In campaign' }, { k: 'backlog', l: 'Backlog (silent)' }].map((t) => (
            <button key={t.k} onClick={() => setFilter(t.k)} data-testid={`overdue-filter-${t.k}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === t.k ? 'bg-cyan-600 text-white' : 'bg-white border border-gray-200 text-gray-600'}`}>{t.l}</button>
          ))}
        </div>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader><CardTitle className="text-gray-900 text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-rose-500" /> Settlements past their expected date</CardTitle></CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
            ) : rows.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No overdue settlements in this view.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <div key={r.booking_id} className="p-4" data-testid={`overdue-row-${r.booking_id}`}>
                    <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-900">{r.talent_name} · {r.gig_name}</p>
                      <p className="text-sm text-gray-500">Worked {r.date_worked ? formatDateFull(r.date_worked) : '—'} • Expected {formatDateFull(r.expected_settlement_date)}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">{r.days_overdue} days overdue</span>
                        {r.status_note ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200">Note posted · auto-notice off</span>
                        ) : r.in_notification_campaign ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Notified {r.notify_count || 0}×</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Backlog · no auto-notice</span>
                        )}
                        {r.has_open_challenge && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">Under review</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="text-lg font-bold text-gray-900 flex items-center gap-1"><DollarSign className="w-4 h-4 text-gray-400" />{fmt(r.settlement_amount).replace('$', '')}</p>
                      <button onClick={() => navigate(`/admin/client/${r.talent_id}`)} className="text-xs text-cyan-600 hover:underline mt-1">View talent</button>
                    </div>
                    </div>
                    {/* Accounting status note — overrides the automatic overdue message */}
                    <div className="mt-3 flex items-center gap-2">
                      <input
                        type="text"
                        defaultValue={r.status_note || ''}
                        placeholder="Accounting status note (e.g. Check mailed 9/12) — shown to talent, stops auto-notices"
                        onChange={(e) => setNoteDrafts((p) => ({ ...p, [r.booking_id]: e.target.value }))}
                        data-testid={`overdue-note-input-${r.booking_id}`}
                        className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5"
                      />
                      <Button size="sm" variant="outline" disabled={savingNote === r.booking_id}
                        onClick={() => saveNote(r.booking_id)} data-testid={`overdue-note-save-${r.booking_id}`}>
                        {savingNote === r.booking_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save note'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
